/**
 * Store d'authentification avec Zustand
 * 
 * Gère l'état d'authentification de manière centralisée
 * avec validation de l'expiration du token JWT
 */

import { create } from "zustand";
import { Auth as ApiAuth } from "../../../shared/services/apiClient";

const TOKEN_KEY = "rema.admin.token";
const USER_KEY = "rema.admin.user";

/**
 * Décode un token JWT pour extraire les informations
 * Un token JWT est composé de 3 parties séparées par des points: header.payload.signature
 * La partie centrale (payload) contient les informations de l'utilisateur encodées en base64
 * Cette fonction décode cette partie pour récupérer les données
 * 
 * @param {string} token - Le token JWT complet
 * @returns {Object|null} Les informations décodées ou null si erreur
 */
function decodeToken(token) {
  try {
    // Un token JWT a la forme: "xxxxx.yyyyy.zzzzz"
    // On sépare les parties par le point
    const parties = token.split(".");
    
    // La partie payload est la deuxième (index 1)
    const partiePayload = parties[1];
    
    if (!partiePayload) {
      return null;
    }
    
    // La payload est encodée en base64url (format spécifique)
    // On remplace les caractères spéciaux pour obtenir du base64 standard
    let base64Standard = partiePayload;
    base64Standard = base64Standard.replace(/-/g, "+");
    base64Standard = base64Standard.replace(/_/g, "/");
    
    // Décoder la chaîne base64 en texte
    const chaineDecodee = atob(base64Standard);
    
    // Convertir chaque caractère en code hexadécimal
    // Cela permet de gérer les caractères spéciaux UTF-8
    let chaineHex = "";
    for (let i = 0; i < chaineDecodee.length; i++) {
      const caractere = chaineDecodee[i];
      const codeCaractere = caractere.charCodeAt(0);
      const codeHex = codeCaractere.toString(16);
      // Ajouter des zéros pour avoir toujours 2 caractères
      const codeHexAvecZeros = "00" + codeHex;
      const deuxDerniersCaracteres = codeHexAvecZeros.slice(-2);
      chaineHex = chaineHex + "%" + deuxDerniersCaracteres;
    }
    
    // Décoder la chaîne hexadécimale en texte UTF-8
    const chaineUtf8 = decodeURIComponent(chaineHex);
    
    // Convertir la chaîne JSON en objet JavaScript
    const payload = JSON.parse(chaineUtf8);
    
    return payload;
  } catch (erreur) {
    // Si une erreur survient pendant le décodage, retourner null
    return null;
  }
}

/**
 * Vérifie si un token est expiré
 * Les tokens JWT contiennent une date d'expiration dans le champ "exp"
 * Cette date est en secondes depuis le 1er janvier 1970 (timestamp Unix)
 * 
 * @param {string} token - Le token JWT à vérifier
 * @returns {boolean} True si le token est expiré, false sinon
 */
function isTokenExpired(token) {
  try {
    // Décoder le token pour récupérer les informations
    const payload = decodeToken(token);
    
    // Si le décodage échoue ou n'a pas de champ d'expiration, on considère qu'il n'est pas expiré
    if (!payload || !payload.exp) {
      return false;
    }
    
    // Récupérer la date actuelle en secondes (timestamp Unix)
    // Date.now() renvoie des millisecondes, on divise par 1000 pour avoir des secondes
    // Math.floor arrondit à l'entier inférieur
    const dateActuelleEnSecondes = Math.floor(Date.now() / 1000);
    
    // Comparer la date d'expiration avec la date actuelle
    // Si exp est inférieur à la date actuelle, le token est expiré
    return payload.exp < dateActuelleEnSecondes;
  } catch (erreur) {
    // En cas d'erreur, on considère que le token est expiré par sécurité
    return true;
  }
}

/**
 * Récupère le token depuis localStorage
 * Cette fonction lit le token stocké dans le navigateur et vérifie s'il est encore valide
 * Si le token est expiré, il est automatiquement supprimé
 * 
 * @returns {string|null} Le token valide ou null s'il n'existe pas ou est expiré
 */
function getToken() {
  try {
    // Lire le token depuis le localStorage
    const token = localStorage.getItem(TOKEN_KEY);
    
    // Si aucun token n'est stocké, retourner null
    if (!token) {
      return null;
    }
    
    // Vérifier si le token est expiré
    const estExpire = isTokenExpired(token);
    
    // Si le token est expiré, le supprimer et retourner null
    if (estExpire) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      return null;
    }
    
    // Le token existe et n'est pas expiré, le retourner
    return token;
  } catch (erreur) {
    // Si une erreur survient (ex: localStorage bloqué), retourner null
    return null;
  }
}

/**
 * Sauvegarde le token dans localStorage
 * Cette fonction stocke le token et extrait les informations utilisateur du token
 * Les informations utilisateur sont aussi stockées pour éviter de décoder le token à chaque fois
 * 
 * @param {string} token - Le token JWT à stocker (ou chaîne vide pour supprimer)
 */
function setToken(token) {
  try {
    // Si un token est fourni, le stocker
    if (token) {
      // Sauvegarder le token dans le localStorage
      localStorage.setItem(TOKEN_KEY, token);
      
      // Décoder le token pour extraire les informations utilisateur
      const payload = decodeToken(token);
      
      // Si le décodage réussit, stocker les informations utilisateur
      if (payload) {
        const informationsUtilisateur = {
          username: payload.username,
          role: payload.role,
        };
        // Convertir l'objet en chaîne JSON pour le stocker
        const utilisateurJson = JSON.stringify(informationsUtilisateur);
        localStorage.setItem(USER_KEY, utilisateurJson);
      }
    } else {
      // Si aucun token n'est fourni (chaîne vide), supprimer tout
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }
  } catch (erreur) {
    // Si une erreur survient (ex: localStorage bloqué), ne rien faire
    // L'application continuera de fonctionner mais ne sauvegardera pas le token
  }
}

/**
 * Supprime le token de localStorage
 * Cette fonction est utilisée lors de la déconnexion pour nettoyer les données
 */
function clearToken() {
  try {
    // Supprimer le token du localStorage
    localStorage.removeItem(TOKEN_KEY);
    
    // Supprimer aussi les informations utilisateur
    localStorage.removeItem(USER_KEY);
  } catch (erreur) {
    // Si une erreur survient, on continue quand même
    // Le but est de nettoyer, mais si on ne peut pas, ce n'est pas grave
  }
}

/**
 * Store Zustand pour l'authentification
 * Zustand est une bibliothèque qui permet de gérer l'état global de l'application
 * Ce store contient toutes les informations liées à l'authentification:
 * - isAuthenticated: si l'utilisateur est connecté
 * - user: les informations de l'utilisateur
 * - loading: si une action est en cours
 * - init: fonction pour initialiser l'état depuis localStorage
 * - login: fonction pour connecter l'utilisateur
 * - logout: fonction pour déconnecter l'utilisateur
 * - checkAuth: fonction pour vérifier l'authentification
 */
export const useAuthStore = create((set) => {
  // État initial: vérifier si un token existe déjà
  const tokenInitial = getToken();
  const estAuthentifieInitialement = !!tokenInitial;
  
  return {
    // État du store
    isAuthenticated: estAuthentifieInitialement,
    user: null,
    loading: false,

    /**
     * Initialise l'état d'authentification depuis localStorage
     * Cette fonction est appelée au démarrage de l'application
     * Elle restaure la session de l'utilisateur s'il était connecté
     */
    init: function() {
      // Récupérer le token depuis localStorage
      const token = getToken();
      
      // Déterminer si l'utilisateur est authentifié
      const estAuthentifie = !!token;
      
      // Initialiser les informations utilisateur à null
      let utilisateur = null;
      
      // Si un token existe, essayer de récupérer les informations utilisateur
      if (token) {
        try {
          const utilisateurJson = localStorage.getItem(USER_KEY);
          if (utilisateurJson) {
            utilisateur = JSON.parse(utilisateurJson);
          }
        } catch (erreur) {
          // Si la lecture échoue, on continue avec utilisateur = null
        }
      }
      
      // Mettre à jour l'état du store
      set({
        isAuthenticated: estAuthentifie,
        user: utilisateur,
      });
    },

    /**
     * Connecte l'utilisateur avec son nom et son mot de passe
     * Cette fonction appelle l'API pour vérifier les identifiants
     * 
     * @param {string} nomUtilisateur - Le nom d'utilisateur
     * @param {string} motDePasse - Le mot de passe
     */
    login: async function(nomUtilisateur, motDePasse) {
      // Indiquer que le chargement commence
      set({ loading: true });
      
      try {
        // Appeler l'API pour se connecter
        const reponseApi = await ApiAuth.login(nomUtilisateur, motDePasse);
        
        // Récupérer le token de la réponse
        const token = reponseApi?.token || "";
        
        // Si aucun token n'est retourné, c'est une erreur
        if (!token) {
          throw new Error("NO_TOKEN");
        }
        
        // Sauvegarder le token dans localStorage
        setToken(token);
        
        // Décoder le token pour récupérer les informations utilisateur
        const payload = decodeToken(token);
        
        // Créer l'objet utilisateur avec les informations du token
        const utilisateur = payload ? {
          username: payload.username,
          role: payload.role,
        } : null;
        
        // Mettre à jour l'état du store
        set({
          isAuthenticated: true,
          user: utilisateur,
          loading: false,
        });
        
        // Retourner la réponse de l'API
        return reponseApi;
      } catch (erreur) {
        // En cas d'erreur, réinitialiser l'état
        set({
          isAuthenticated: false,
          user: null,
          loading: false,
        });
        
        // Propager l'erreur pour que le code appelant puisse la gérer
        throw erreur;
      }
    },

    /**
     * Déconnecte l'utilisateur
     * Cette fonction supprime le token et réinitialise l'état
     */
    logout: function() {
      // Appeler la fonction de déconnexion de l'API
      ApiAuth.logout();
      
      // Supprimer le token du localStorage
      clearToken();
      
      // Réinitialiser l'état du store
      set({
        isAuthenticated: false,
        user: null,
      });
    },

    /**
     * Vérifie si l'utilisateur est authentifié
     * Cette fonction est utilisée pour vérifier la session actuelle
     * 
     * @returns {boolean} True si l'utilisateur est authentifié
     */
    checkAuth: function() {
      // Récupérer le token depuis localStorage
      const token = getToken();
      
      // Déterminer si l'utilisateur est authentifié
      const estAuthentifie = !!token;
      
      // Mettre à jour l'état du store
      set({ isAuthenticated: estAuthentifie });
      
      // Retourner le résultat
      return estAuthentifie;
    },
  };
});

// Initialiser le store au chargement de l'application
// Cela restaure la session si l'utilisateur était connecté
useAuthStore.getState().init();
